import {inject} from '@angular/core';
import {CanMatchFn, Route, Router} from '@angular/router';
import {JwtToken} from '@core/api/data-access';
import {AuthStore} from '../../+store/auth.store';
import {isLegacyEnvironmentRedirectUrlWithAuthParams, LEGACY_ENV_NAVIGATION_URL_PARAMS} from '../../types/navigation-management.types';

/**
 * Extracts user information from access token in URL parameters
 */
function getUserFromUrlToken(urlParams: URLSearchParams): {userId: string; username: string} | null {
	const accessToken = urlParams.get(LEGACY_ENV_NAVIGATION_URL_PARAMS.accessToken) || urlParams.get('accessToken');
	if (!accessToken) return null;
	
	try {
		const decoded = new JwtToken(accessToken);
		return {
			userId: decoded.nameid,
			username: decoded.unique_name
		};
	} catch {
		return null;
	}
}

export function LoginMatcher(): CanMatchFn {
	return (route: Route) => {
		const authStore: AuthStore = inject(AuthStore);
		const router: Router = inject(Router);
		const urlParams = new URLSearchParams(window.location.search);
		const urlParamsObject = Object.fromEntries(urlParams.entries());
		
		// Check if there are legacy auth parameters in the URL
		const hasLegacyAuthParams = isLegacyEnvironmentRedirectUrlWithAuthParams(urlParamsObject);
		
		if (authStore.isAuthenticated()) {
			// If user is authenticated but there are different user credentials in URL, 
			// allow login process to handle the logout and re-login
			if (hasLegacyAuthParams) {
				const urlUser = getUserFromUrlToken(urlParams);
				if (urlUser) {
					const currentToken = authStore.accessToken();
					try {
						const currentUser = new JwtToken(currentToken);
						const isDifferentUser = currentUser.nameid !== urlUser.userId || currentUser.unique_name !== urlUser.username;
						
						if (isDifferentUser) {
							// This should trigger the logout and re-login process
							return true;
						}
					} catch {
						// If there's an error decoding the token, allow the process to continue
						return true;
					}
				}
			}
			// Normal case: authenticated user without different credentials in URL
			if (route.path !== 'home') router.navigate(['home']);
			return false;
		}
		// navigate to 'login' page if the user not in login page
		else if (route.path === 'login') return true;
		router.navigate(['login']);
		return false;
	};
}
